/**
 * useSessionExpiration Hook
 * 
 * Listens for the custom `session:expired` event fired by the
 * global error handler and provides state for showing a
 * session-expired toast / overlay.
 * 
 * Usage:
 *   const { sessionExpired, message, dismiss } = useSessionExpiration();
 */

import { useCallback, useEffect, useState } from 'react';
import { SESSION_EXPIRED_EVENT, SessionExpiredDetail } from '../errors/globalErrorHandlers';

export interface SessionExpirationState {
    /** True when a 401 / session-expired event has been fired */
    sessionExpired: boolean;
    /** Human-readable message from the error handler */
    message: string;
    /** Dismiss the notification (reset state) */
    dismiss: () => void;
}

export function useSessionExpiration(): SessionExpirationState {
    const [sessionExpired, setSessionExpired] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<SessionExpiredDetail>).detail;
            setSessionExpired(true);
            setMessage(detail?.message || 'Session expired – please sign in again.');
        };

        window.addEventListener(SESSION_EXPIRED_EVENT, handler);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
    }, []);

    const dismiss = useCallback(() => {
        setSessionExpired(false);
        setMessage('');
    }, []);

    return { sessionExpired, message, dismiss };
}

export default useSessionExpiration;
